import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtCurrency, useSettings } from "@/lib/utils-app";
import { Users, TrendingUp, AlertTriangle, Clock, BarChart3, UserCheck } from "lucide-react";
import { fmtDateTime } from "@/lib/utils-app";
import SubscriberAvatar from "@/components/SubscriberAvatar";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";

const RANGE_OPTIONS = [
  { id: "today", label: "Hoy" },
  { id: "last_7", label: "Últimos 7 días" },
  { id: "month", label: "Mes actual" },
  { id: "prev_month", label: "Mes anterior" },
  { id: "ytd", label: "Totales" },
];

function RangeFilters({ value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid={testId}>
      {RANGE_OPTIONS.map((option) => {
        const active = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={`text-[11px] uppercase tracking-wider px-3 py-1.5 border rounded-sm ${
              active ? "border-[#E63946] text-white bg-[#E63946]/15" : "border-white/15 text-white/50 hover:text-white"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function chartMargin(pointCount) {
  return { top: 28, right: 8, left: 0, bottom: pointCount > 12 ? 8 : 0 };
}

function barCountLabel(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? String(n) : "";
}

function barMoneyLabel(value, symbol) {
  const n = Number(value || 0);
  if (!n) return "";
  return `${symbol}${n.toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [attendanceRange, setAttendanceRange] = useState("last_7");
  const [packageRange, setPackageRange] = useState("ytd");
  const settings = useSettings();
  const sym = settings.currency_symbol || "$";

  useEffect(() => { api.get("/admin/dashboard").then(r => setStats(r.data)).catch(() => {}); }, []);

  if (!stats) return <div className="text-white/40">Cargando…</div>;

  const cards = [
    { label: "Suscriptores activos", value: stats.active_subscribers, total: `de ${stats.total_subscribers}`, icon: Users, accent: "text-[#FFD700]" },
    { label: "Ingresos del mes", value: fmtCurrency(stats.month_revenue, sym), icon: TrendingUp, accent: "text-green-400" },
    { label: "Pagos vencidos", value: stats.overdue_count, total: fmtCurrency(stats.overdue_amount, sym), icon: AlertTriangle, accent: "text-red-400" },
    { label: "Renovaciones 7 días", value: stats.upcoming_count, icon: Clock, accent: "text-yellow-400" },
    { label: "Asistencias hoy", value: stats.attendance_today ?? 0, icon: UserCheck, accent: "text-green-400" },
  ];

  const attendanceSeries = stats.attendance_ranges?.[attendanceRange]
    || (attendanceRange === "last_7" ? (stats.attendance_last_7_days || []).map((row) => ({ label: row.date, count: row.count })) : []);
  const packageSeries = stats.package_ranges?.[packageRange]
    || (packageRange === "ytd" ? (stats.package_breakdown || []).map((row) => ({ name: row.name, revenue: row.revenue })) : []);
  const yearSeries = stats.revenue_by_month || [];
  const attendanceTilt = attendanceSeries.length > 12;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Resumen</div>
          <h1 className="font-display text-4xl md:text-5xl text-white mt-1">Dashboard</h1>
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="glass p-5 rounded-sm" data-testid="metric-card">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-white/60">{c.label}</div>
              <c.icon size={18} className={c.accent} />
            </div>
            <div className={`font-display text-4xl mt-3 ${c.accent}`}>{c.value}</div>
            {c.total && <div className="text-xs text-white/40 mt-1">{c.total}</div>}
          </div>
        ))}
      </div>

      <div className="mt-8 glass p-6 rounded-sm" data-testid="year-revenue-chart">
        <h2 className="font-display text-2xl text-white mb-4">Ingresos del año</h2>
        {yearSeries.length === 0 ? (
          <div className="text-white/40 py-12 text-center">Aún no hay datos de pagos.</div>
        ) : (
          <div className="h-72 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={yearSeries} margin={chartMargin(yearSeries.length)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 12 }} />
                <YAxis tick={{ fill: "#888", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #222", color: "#fff" }} formatter={(v) => fmtCurrency(v, sym)} />
                <Bar dataKey="revenue" fill="#FFD700" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="revenue" position="top" formatter={(v) => barMoneyLabel(v, sym)} style={{ fill: "#fff", fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="mt-8 glass p-6 rounded-sm" data-testid="attendance-chart">
        <h2 className="font-display text-2xl text-white mb-4">Asistencias</h2>
        <RangeFilters value={attendanceRange} onChange={setAttendanceRange} testId="attendance-range-filters" />
        <div className="h-72 min-w-0">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={attendanceSeries} margin={chartMargin(attendanceSeries.length)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis
                dataKey="label"
                interval={0}
                angle={attendanceTilt ? -40 : 0}
                textAnchor={attendanceTilt ? "end" : "middle"}
                height={attendanceTilt ? 64 : 30}
                tick={{ fill: "#888", fontSize: 11 }}
              />
              <YAxis allowDecimals={false} tick={{ fill: "#888", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #222", color: "#fff" }} />
              <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="count" position="top" formatter={barCountLabel} style={{ fill: "#fff", fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {stats.recent_check_ins?.length > 0 && (
        <div className="mt-8 glass p-6 rounded-sm">
          <h2 className="font-display text-2xl text-white mb-4">Últimos check-ins</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-white/40 border-b border-theme-secondary/10">
                  <th className="p-2">Hora</th>
                  <th className="p-2">Atleta</th>
                  <th className="p-2">Turno</th>
                  <th className="p-2">Método</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_check_ins.slice(0, 10).map((a) => (
                  <tr key={a.id} className="border-b border-theme-secondary/10 text-white/60">
                    <td className="p-2">{fmtDateTime(a.check_in_at)}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <SubscriberAvatar
                          subscriber={{ profile_photo: a.profile_photo, has_profile_photo: !!a.profile_photo }}
                          size={32}
                        />
                        <span>{a.subscriber_name}</span>
                      </div>
                    </td>
                    <td className="p-2">{a.shift_name || "—"}</td>
                    <td className="p-2 capitalize">{a.method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 glass p-6 rounded-sm" data-testid="package-revenue-chart">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={18} className="text-[#E63946]" />
          <h2 className="font-display text-2xl text-white">Ingresos por paquete</h2>
        </div>
        <RangeFilters value={packageRange} onChange={setPackageRange} testId="package-range-filters" />
        {packageSeries.length === 0 ? (
          <div className="text-white/40 py-12 text-center">Aún no hay datos de pagos.</div>
        ) : (
          <div className="h-80 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={packageSeries} margin={{ top: 28, right: 8, left: 0, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="name" interval={0} angle={-25} textAnchor="end" height={70} tick={{ fill: "#888", fontSize: 11 }} />
                <YAxis tick={{ fill: "#888", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #222", color: "#fff" }} formatter={(v) => fmtCurrency(v, sym)} />
                <Bar dataKey="revenue" fill="#E63946" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="revenue" position="top" formatter={(v) => barMoneyLabel(v, sym)} style={{ fill: "#fff", fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
